import { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import type { AdminAreaView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@admin/app/locale";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { Banner, Button, Modal, Toggle, useToast } from "@/shared/rd";
import { useUpdateAreaMutation } from "../services/useUpdateAreaMutation";
import { useDeleteAreaMutation } from "../services/useDeleteAreaMutation";
import { AreaEditor } from "./AreaEditor";

/** Area · Status · actions — shared with the table head in AreasPage. */
export const AREA_ROW_COLUMNS = "minmax(0,2fr) minmax(0,1fr) 96px";

/**
 * One area, as a row of the governorate table: the localized name over its
 * other-language twin, an inline active switch in the Status column, and the
 * edit / delete actions at the trailing edge.
 *
 * The Arabic name is the source of truth, so it is the one that always shows;
 * the second line is whichever name is not the page's language.
 *
 * Pausing/activating is reversible (the same PATCH), so it happens at once
 * with an Undo toast. Delete can't be undone, so it stays behind a modal.
 * A failure is a Banner under the row (or in the modal), never a toast.
 */
export function AreaTableRow({ area }: { area: AdminAreaView }) {
  const t = useTranslations();
  const toast = useToast();
  const locale = useLocale();
  const updateArea = useUpdateAreaMutation();
  const deleteArea = useDeleteAreaMutation();
  /** The failed switch: what it tried to set, and the server's words. */
  const [toggleError, setToggleError] = useState<{ next: boolean; detail: string } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const isPending = updateArea.isPending || deleteArea.isPending;
  const primary = locale === "ar" ? area.nameAr : (area.nameEn ?? area.nameAr);
  const secondary = locale === "ar" ? area.nameEn : area.nameAr;

  async function toggleActive(next: boolean, offerUndo = true): Promise<void> {
    setToggleError(null);
    try {
      await updateArea.mutateAsync({ id: area.id, isActive: next });
    } catch (err) {
      setToggleError({ next, detail: detailFor(err) });
      return;
    }
    toast.show(
      offerUndo
        ? {
            tone: "neutral",
            message: t(next ? "areas.toastActivated" : "areas.toastPaused", { name: primary }),
            actionLabel: t("areas.undo"),
            onAction: () => void toggleActive(!next, false),
          }
        : { tone: "neutral", message: t(next ? "areas.toastActivated" : "areas.toastPaused", { name: primary }) },
    );
  }

  async function performDelete(): Promise<void> {
    setDeleteError(null);
    try {
      await deleteArea.mutateAsync({ id: area.id });
    } catch (err) {
      setDeleteError(detailFor(err));
      return;
    }
    setIsConfirmOpen(false);
    toast.show({ message: t("areas.toastDeleted", { name: primary }) });
  }

  return (
    <>
      <div className="rd-table-row" role="row" style={{ gridTemplateColumns: AREA_ROW_COLUMNS }}>
        <div className="rd-cell rd-cell-stack" role="cell" style={{ paddingInlineStart: 28 }}>
          <span style={{ fontWeight: 500 }} dir="auto">
            {primary}
          </span>
          {secondary ? (
            <span className="rd-cell-sub" dir="auto">
              {secondary}
            </span>
          ) : null}
        </div>

        <div className="rd-cell" role="cell" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Toggle
            size="sm"
            checked={area.isActive}
            disabled={isPending}
            label={`${t(keys.areas.activeLabel)}: ${primary}`}
            onChange={(next) => void toggleActive(next)}
          />
          <span style={{ fontSize: 13, color: "var(--rd-text-secondary)" }}>
            {t(area.isActive ? keys.common.active : keys.areas.statusPaused)}
          </span>
        </div>

        <div className="rd-cell rd-cell-end" role="cell" style={{ gap: 2 }}>
          <Button
            variant="ghost"
            iconOnly
            icon={Pencil}
            title={`${t(keys.common.edit)}: ${primary}`}
            disabled={isPending}
            onClick={() => setIsEditOpen(true)}
          />
          <Button
            variant="ghost"
            iconOnly
            icon={Trash2}
            title={`${t(keys.common.delete)}: ${primary}`}
            disabled={isPending}
            onClick={() => {
              setDeleteError(null);
              setIsConfirmOpen(true);
            }}
          />
        </div>
      </div>

      {toggleError ? (
        <div style={{ padding: "8px 20px", paddingInlineStart: 36, borderBottom: "1px solid var(--rd-line-row)" }}>
          <Banner
            tone="error"
            title={t(toggleError.next ? "areas.activateFailedTitle" : "areas.pauseFailedTitle", { name: primary })}
            actions={
              <Button
                size="sm"
                loading={updateArea.isPending}
                loadingLabel={t(keys.shell.retrying)}
                onClick={() => void toggleActive(toggleError.next)}
              >
                {t(keys.shell.retry)}
              </Button>
            }
            onDismiss={() => setToggleError(null)}
            dismissLabel={t(keys.common.close)}
          >
            {[toggleError.detail, t("areas.nothingChanged")].filter(Boolean).join(" ")}
          </Banner>
        </div>
      ) : null}

      {isEditOpen ? <AreaEditor area={area} onClose={() => setIsEditOpen(false)} /> : null}

      <Modal
        open={isConfirmOpen}
        onClose={deleteArea.isPending ? undefined : () => setIsConfirmOpen(false)}
        title={t(keys.areas.deleteAction)}
        width={400}
        actions={
          <>
            <Button variant="ghost" muted disabled={deleteArea.isPending} onClick={() => setIsConfirmOpen(false)}>
              {t(keys.common.cancel)}
            </Button>
            <Button
              variant="danger"
              loading={deleteArea.isPending}
              loadingLabel={t("areas.deleting")}
              onClick={() => void performDelete()}
            >
              {deleteError !== null ? t(keys.shell.retry) : t(keys.common.delete)}
            </Button>
          </>
        }
      >
        {deleteError !== null ? (
          <Banner tone="error" title={t("areas.deleteFailedTitle", { name: primary })}>
            {[deleteError, t("areas.nothingChanged")].filter(Boolean).join(" ")}
          </Banner>
        ) : null}
        <p className="rd-modal-text" style={{ margin: 0 }}>
          {t(keys.areas.confirmDelete)}
        </p>
      </Modal>
    </>
  );
}

/** The server's own words when it gave any; the banner says the rest. */
function detailFor(err: unknown): string {
  return err instanceof ApiJsonClientError ? err.message || "" : "";
}
